import { useState, useEffect, useMemo } from 'react';
import { ListChecks, FlaskConical, Check, TrendingUp, Calculator, Leaf } from 'lucide-react';
import { useLab } from '../../../context/LabContext';
import { Spectrophotometer, TestTube } from '../../lab';
import InfoBox from '../InfoBox';

const steps = [
  { id: 1, title: 'Set Wavelength', short: 'Set to 510 nm' },
  { id: 2, title: 'Zero with Blank', short: 'Insert blank and zero' },
  { id: 3, title: 'Measure Standards', short: 'Measure each standard' },
  { id: 4, title: 'Plot Calibration Curve', short: 'View calibration' },
  { id: 5, title: 'Measure Sample', short: 'Measure soil extract' },
  { id: 6, title: 'Calculate Iron Content', short: 'Determine Fe concentration' }
];

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'Beer-Lambert at 510 nm',
    content: 'The molar absorptivity (epsilon) of [Fe(phen)3]2+ is 11,100 L/mol cm at 510 nm - very high! This gives excellent sensitivity for iron detection at ppm levels.'
  },
  4: {
    title: 'Linear Regression',
    content: 'The calibration curve plots absorbance (y) vs concentration (x). Linear regression gives A = slope * c + intercept. A good R-squared (> 0.99) indicates a linear relationship.'
  },
  6: {
    title: 'Soil Iron',
    content: 'Iron is essential for plant growth, involved in chlorophyll synthesis and electron transport. Soil typically contains 2-5% iron, but only a fraction is available to plants.'
  }
};

// Simulated absorbance values for iron-phenanthroline at 510 nm
const getAbsorbance = (ppm: number): number => {
  // A = epsilon * b * c, with extinction coefficient giving ~0.22 A per ppm
  return Number((0.22 * ppm).toFixed(3));
};

// Get color based on concentration (orange-red)
const getColorIntensity = (ppm: number): string => {
  const maxPpm = 4.0;
  const intensity = Math.min(ppm / maxPpm, 1);
  const r = 255;
  const g = Math.round(200 - intensity * 100);
  const b = Math.round(180 - intensity * 130);
  return `rgb(${r}, ${g}, ${b})`;
};

// Linear regression calculation
const linearRegression = (points: { x: number; y: number }[]) => {
  const n = points.length;
  if (n < 2) return { slope: 0, intercept: 0, r2: 0 };

  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // Calculate R-squared
  const meanY = sumY / n;
  const ssTotal = points.reduce((s, p) => s + Math.pow(p.y - meanY, 2), 0);
  const ssResidual = points.reduce((s, p) => s + Math.pow(p.y - (slope * p.x + intercept), 2), 0);
  const r2 = ssTotal > 0 ? 1 - ssResidual / ssTotal : 0;

  return { slope, intercept, r2 };
};

const Part3Measurement = () => {
  const { state, updateExp9Part3, completeStep, update } = useLab();
  const { exp9Part1, exp9Part2, exp9Part3, currentStep, labMode, completedSteps } = state;

  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [selectedTube, setSelectedTube] = useState<string | null>(null);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);

  // Fix: Reset currentStep to first incomplete step when this part mounts
  useEffect(() => {
    if (labMode !== 'guided') return;
    const firstIncomplete = steps.find(s => !completedSteps.includes(`measurement-${s.id}`));
    const targetStep = firstIncomplete ? firstIncomplete.id : steps[steps.length - 1].id;
    if (currentStep !== targetStep) {
      update({ currentStep: targetStep });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (s: number) => labMode === 'free' || currentStep === s;
  const isDone = (s: number) => completedSteps.includes(`measurement-${s}`);

  // Check if color development is complete from Part 2
  const colorDevelopmentComplete = exp9Part2.colorDeveloped && exp9Part2.standards.every((s: { prepared: boolean }) => s.prepared);

  // Get list of standards with absorbance
  const standards = useMemo(() => {
    return exp9Part2.standards.map((s: { ppm: number; prepared: boolean }, i: number) => ({
      ...s,
      id: `std${i}`,
      absorbance: getAbsorbance(s.ppm),
      measured: exp9Part3.readings[`std${i}`] !== undefined
    }));
  }, [exp9Part2.standards, exp9Part3.readings]);

  // Calculate calibration data
  const calibrationData = useMemo(() => {
    const measuredStandards = standards.filter((s: { measured: boolean; ppm: number }) => s.measured && s.ppm > 0);
    if (measuredStandards.length < 2) return null;

    const points = measuredStandards.map((s: { ppm: number; absorbance: number }) => ({ x: s.ppm, y: s.absorbance }));
    return linearRegression(points);
  }, [standards]);

  // Handler: Set wavelength
  const handleSetWavelength = () => {
    if (!isActive(1)) return;
    updateExp9Part3({ wavelength: 510 });
    completeStep('measurement-1');
  };

  // Handler: Zero with blank
  const handleZero = () => {
    if (!isActive(2) || !isDone(1)) return;
    setSelectedTube('std0');
    setTimeout(() => {
      updateExp9Part3({
        isZeroed: true,
        currentReading: 0,
        readings: { ...exp9Part3.readings, std0: 0 }
      });
      completeStep('measurement-2');
    }, 1000);
  };

  // Handler: Measure standard
  const handleMeasureStandard = (stdId: string, ppm: number) => {
    if (!isActive(3) || !exp9Part3.isZeroed || stdId === 'std0') return;
    setSelectedTube(stdId);
    const absorbance = getAbsorbance(ppm);

    setTimeout(() => {
      updateExp9Part3({
        currentReading: absorbance,
        readings: { ...exp9Part3.readings, [stdId]: absorbance }
      });

      // Check if all standards measured
      const newReadings = { ...exp9Part3.readings, [stdId]: absorbance };
      const allMeasured = standards.every((s: { id: string }) => newReadings[s.id] !== undefined);
      if (allMeasured) {
        completeStep('measurement-3');
      }
    }, 800);
  };

  // Handler: Plot calibration
  const handlePlotCalibration = () => {
    if (!isActive(4) || !isDone(3)) return;
    if (calibrationData) {
      updateExp9Part3({
        calibrationComplete: true,
        calibrationData: calibrationData
      });
      setShowCalibrationModal(true);
      completeStep('measurement-4');
    }
  };

  // Handler: Measure sample
  const handleMeasureSample = () => {
    if (!isActive(5) || !exp9Part3.calibrationComplete) return;
    setSelectedTube('sample');
    const sampleAbsorbance = 0.55; // ~2.5 ppm in solution

    setTimeout(() => {
      updateExp9Part3({
        currentReading: sampleAbsorbance,
        readings: { ...exp9Part3.readings, sample: sampleAbsorbance }
      });
      completeStep('measurement-5');
    }, 800);
  };

  // Handler: Calculate iron content
  const handleCalculate = () => {
    if (!isActive(6) || !exp9Part3.readings.sample) return;

    const { slope, intercept } = exp9Part3.calibrationData;
    if (slope) {
      // Calculate ppm in solution
      const ppmInSolution = (exp9Part3.readings.sample - intercept) / slope;

      // Calculate mg/kg in soil (considering dilutions)
      // Assuming: 1g soil -> 100mL extract -> 25mL aliquot -> 100mL final
      // Dilution factor = 100/25 = 4, so ppm in extract = ppm * 4
      // mg Fe per kg soil = (ppm in extract * 100mL) / (1g soil) * 1000 = ppm * 400
      const mgPerKg = ppmInSolution * 4 * 100; // mg Fe per kg soil

      updateExp9Part3({
        ironConcentration: {
          ppmInSolution: Number(ppmInSolution.toFixed(2)),
          mgPerKg: Number(mgPerKg.toFixed(0)),
          percentIron: Number((mgPerKg / 10000).toFixed(3))
        }
      });
      setShowResultModal(true);
      completeStep('measurement-6');
    }
  };

  const completedCount = completedSteps.filter((s: string) => s.startsWith('measurement')).length;

  return (
    <div className="part3-measurement">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part III: Measurement</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  {stepInfoContent[s.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(s.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Spectrophotometric Analysis - Iron</h3>
          </div>
          <span className="bench-progress">{completedCount}/6 Complete</span>
        </div>

        <div className="bench-area">
          {!colorDevelopmentComplete && (
            <div className="warning-message">
              Color development not complete. Please complete Part II first.
            </div>
          )}

          {colorDevelopmentComplete && (
            <>
              {/* Spectrophotometer */}
              <div className="spectrophotometer-section">
                <Spectrophotometer
                  wavelength={exp9Part3.wavelength}
                  absorbance={exp9Part3.currentReading || 0}
                  sampleIn={selectedTube !== null}
                  onInsertSample={
                    isActive(1) && !isDone(1) ? handleSetWavelength :
                    isActive(2) && !isDone(2) ? handleZero : undefined
                  }
                />

                <div className="spec-controls">
                  <div className="wavelength-display">
                    <span>Wavelength:</span>
                    <strong>{exp9Part3.wavelength} nm</strong>
                    {!isDone(1) && (
                      <button
                        className="set-wavelength-btn"
                        onClick={handleSetWavelength}
                        disabled={labMode === 'guided' && !isActive(1)}
                      >
                        Set 510 nm
                      </button>
                    )}
                  </div>

                  {isDone(1) && !isDone(2) && (
                    <button
                      className="zero-btn"
                      onClick={handleZero}
                      disabled={labMode === 'guided' && !isActive(2)}
                    >
                      Zero with Blank
                    </button>
                  )}

                  {exp9Part3.isZeroed && (
                    <div className="zeroed-indicator">
                      <Check size={14} />
                      <span>Zeroed</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Sample Tubes */}
              <div className="samples-section">
                <h4>Standards & Sample</h4>
                <div className="samples-row">
                  {standards.map((std: { id: string; ppm: number; measured: boolean; absorbance: number }, index: number) => (
                    <div
                      key={std.id}
                      className={`sample-container ${selectedTube === std.id ? 'selected' : ''}`}
                    >
                      <TestTube
                        fillLevel={70}
                        fillColor={getColorIntensity(std.ppm)}
                        label={std.ppm === 0 ? 'Blank' : `${std.ppm} ppm`}
                        onClick={() => index > 0 && handleMeasureStandard(std.id, std.ppm)}
                        active={isActive(3) && !std.measured && index > 0 && exp9Part3.isZeroed}
                        disabled={labMode === 'guided' && !isActive(index === 0 ? 2 : 3)}
                        hasResult={std.measured}
                      />
                      {std.measured && (
                        <div className="reading-badge">
                          A = {std.absorbance.toFixed(3)}
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="sample-divider" />

                  <div className={`sample-container ${selectedTube === 'sample' ? 'selected' : ''}`}>
                    <TestTube
                      fillLevel={70}
                      fillColor={getColorIntensity(2.5)}
                      label="Soil Sample"
                      onClick={handleMeasureSample}
                      active={isActive(5) && !exp9Part3.readings.sample}
                      disabled={labMode === 'guided' && !isActive(5)}
                      hasResult={exp9Part3.readings.sample !== undefined}
                    />
                    {exp9Part3.readings.sample !== undefined && (
                      <div className="reading-badge sample">
                        A = {exp9Part3.readings.sample.toFixed(3)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                {isDone(3) && !isDone(4) && (
                  <button
                    className="action-btn calibration"
                    onClick={handlePlotCalibration}
                  >
                    <TrendingUp size={18} />
                    Plot Calibration Curve
                  </button>
                )}

                {isDone(5) && !isDone(6) && (
                  <button
                    className="action-btn calculate"
                    onClick={handleCalculate}
                  >
                    <Calculator size={18} />
                    Calculate Iron Content
                  </button>
                )}
              </div>

              {/* Final Result */}
              {exp9Part3.ironConcentration && (
                <div className="final-result">
                  <h3>Results</h3>
                  <div className="result-grid">
                    <div className="result-item">
                      <span>Solution Concentration:</span>
                      <strong>{exp9Part3.ironConcentration.ppmInSolution} ppm Fe</strong>
                    </div>
                    <div className="result-item">
                      <span>Iron in Soil:</span>
                      <strong>{exp9Part3.ironConcentration.mgPerKg} mg/kg</strong>
                    </div>
                    <div className="result-item highlight">
                      <span>Percent Iron:</span>
                      <strong>{exp9Part3.ironConcentration.percentIron}%</strong>
                    </div>
                  </div>
                  <div className="result-interpretation">
                    <Leaf size={16} />
                    <span>
                      {exp9Part3.ironConcentration.percentIron < 2
                        ? 'Low iron - may need supplementation for plant growth'
                        : exp9Part3.ironConcentration.percentIron <= 5
                          ? 'Normal iron range for most soils'
                          : 'High iron content - typical for iron-rich soils'}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Bench Surface */}
          <div className="bench-surface" />
        </div>
      </div>

      {/* Calibration Modal */}
      {showCalibrationModal && calibrationData && (
        <div className="modal-overlay" onClick={() => setShowCalibrationModal(false)}>
          <div className="modal-content calibration-modal" onClick={e => e.stopPropagation()}>
            <h3>Calibration Curve - Fe-Phenanthroline</h3>
            <div className="calibration-chart">
              <svg viewBox="0 0 300 200" className="chart-svg">
                {/* Axes */}
                <line x1="40" y1="160" x2="280" y2="160" stroke="var(--text-secondary)" strokeWidth="2" />
                <line x1="40" y1="20" x2="40" y2="160" stroke="var(--text-secondary)" strokeWidth="2" />

                {/* Grid lines */}
                {[0.2, 0.4, 0.6, 0.8].map((y, i) => (
                  <g key={i}>
                    <line
                      x1="40" y1={160 - y * 175}
                      x2="280" y2={160 - y * 175}
                      stroke="var(--border)" strokeDasharray="4"
                    />
                    <text x="35" y={165 - y * 175} fill="var(--text-muted)" fontSize="10" textAnchor="end">
                      {y.toFixed(1)}
                    </text>
                  </g>
                ))}

                {/* X-axis labels */}
                {[1, 2, 3, 4].map((x, i) => (
                  <text key={i} x={40 + x * 60} y="175" fill="var(--text-muted)" fontSize="10" textAnchor="middle">
                    {x}
                  </text>
                ))}

                {/* Regression line */}
                <line
                  x1="40"
                  y1={160 - calibrationData.intercept * 175}
                  x2="280"
                  y2={160 - (calibrationData.slope * 4.0 + calibrationData.intercept) * 175}
                  stroke="#FF6432"
                  strokeWidth="2"
                />

                {/* Data points */}
                {standards.filter((s: { measured: boolean; ppm: number }) => s.measured && s.ppm > 0).map((s: { ppm: number; absorbance: number }, i: number) => (
                  <circle
                    key={i}
                    cx={40 + s.ppm * 60}
                    cy={160 - s.absorbance * 175}
                    r="6"
                    fill="var(--primary)"
                  />
                ))}

                {/* Axis labels */}
                <text x="160" y="195" fill="var(--text-primary)" fontSize="12" textAnchor="middle">
                  Fe Concentration (ppm)
                </text>
                <text x="15" y="90" fill="var(--text-primary)" fontSize="12" textAnchor="middle" transform="rotate(-90, 15, 90)">
                  Absorbance
                </text>
              </svg>
            </div>
            <div className="calibration-stats">
              <div>Slope: {calibrationData.slope.toFixed(4)}</div>
              <div>Intercept: {calibrationData.intercept.toFixed(4)}</div>
              <div>R<sup>2</sup>: {calibrationData.r2.toFixed(4)}</div>
            </div>
            <button className="modal-close-btn" onClick={() => setShowCalibrationModal(false)}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showResultModal && exp9Part3.ironConcentration && (
        <div className="modal-overlay" onClick={() => setShowResultModal(false)}>
          <div className="modal-content result-modal" onClick={e => e.stopPropagation()}>
            <div className="result-icon">
              <Calculator size={32} />
            </div>
            <h3>Iron Content Calculated</h3>
            <div className="calculation-steps">
              <p>From calibration curve:</p>
              <div className="equation">A = {exp9Part3.calibrationData.slope?.toFixed(4)} * c + {exp9Part3.calibrationData.intercept?.toFixed(4)}</div>
              <div className="equation">{exp9Part3.readings.sample?.toFixed(3)} = {exp9Part3.calibrationData.slope?.toFixed(4)} * c + {exp9Part3.calibrationData.intercept?.toFixed(4)}</div>
              <div className="equation">c = {exp9Part3.ironConcentration.ppmInSolution} ppm (in solution)</div>
              <div className="divider" />
              <p>Converting to soil content:</p>
              <div className="equation small">mg Fe/kg soil = ppm * dilution factor * volume / sample mass</div>
              <div className="equation result">
                Iron in soil: {exp9Part3.ironConcentration.mgPerKg} mg/kg ({exp9Part3.ironConcentration.percentIron}%)
              </div>
            </div>
            <button className="modal-close-btn" onClick={() => setShowResultModal(false)}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp9"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}

      <style>{`
        .part3-measurement {
          display: flex;
          gap: 16px;
          flex: 1;
          width: 100%;
          min-height: 0;
          padding: 16px;
        }

        .steps-panel {
          width: 240px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 12px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
          border: none;
          cursor: pointer;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          display: block;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 24px;
          gap: 24px;
          overflow-y: auto;
        }

        .warning-message {
          background: rgba(239, 68, 68, 0.15);
          color: var(--danger);
          padding: 16px 24px;
          border-radius: 10px;
          font-size: 14px;
        }

        .spectrophotometer-section {
          display: flex;
          align-items: flex-start;
          gap: 24px;
        }

        .spec-controls {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .wavelength-display {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--bg-dark);
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 13px;
        }

        .wavelength-display strong {
          color: #FF6432;
        }

        .set-wavelength-btn, .zero-btn {
          padding: 10px 16px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .set-wavelength-btn {
          background: var(--primary);
          color: white;
          border: none;
          margin-left: 10px;
        }

        .zero-btn {
          background: var(--bg-elevated);
          border: 2px solid var(--primary);
          color: var(--primary);
        }

        .zero-btn:hover {
          background: var(--primary);
          color: white;
        }

        .zeroed-indicator {
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--secondary);
          font-size: 13px;
          font-weight: 600;
        }

        .samples-section {
          text-align: center;
          width: 100%;
        }

        .samples-section h4 {
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 16px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .samples-row {
          display: flex;
          gap: 16px;
          justify-content: center;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        .sample-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .sample-container.selected {
          background: rgba(255, 100, 50, 0.15);
        }

        .sample-divider {
          width: 2px;
          height: 80px;
          background: var(--border);
          margin: 0 8px;
        }

        .reading-badge {
          background: var(--bg-dark);
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-family: 'Courier New', monospace;
          color: var(--text-secondary);
        }

        .reading-badge.sample {
          background: #FF6432;
          color: white;
        }

        .action-buttons {
          display: flex;
          gap: 16px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 24px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }

        .action-btn.calibration {
          background: #FF6432;
          color: white;
        }

        .action-btn.calculate {
          background: var(--primary);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .final-result {
          background: var(--bg-elevated);
          border: 2px solid #FF6432;
          border-radius: 16px;
          padding: 24px;
          text-align: center;
          max-width: 450px;
          width: 100%;
        }

        .final-result h3 {
          color: #FF6432;
          margin-bottom: 16px;
        }

        .result-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 16px;
        }

        .result-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px;
          background: var(--bg-dark);
          border-radius: 8px;
        }

        .result-item span {
          font-size: 11px;
          color: var(--text-muted);
        }

        .result-item strong {
          font-size: 16px;
          color: var(--text-primary);
        }

        .result-item.highlight {
          background: #FF6432;
        }

        .result-item.highlight span,
        .result-item.highlight strong {
          color: white;
        }

        .result-interpretation {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px;
          background: var(--bg-dark);
          border-radius: 8px;
          font-size: 12px;
          color: var(--text-secondary);
        }

        .result-interpretation svg {
          color: var(--secondary);
        }

        .bench-surface {
          display: none;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: var(--bg-surface);
          border-radius: 16px;
          padding: 24px;
          max-width: 500px;
          width: 90%;
          text-align: center;
        }

        .modal-content h3 {
          margin-bottom: 20px;
        }

        .calibration-chart {
          background: var(--bg-dark);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .chart-svg {
          width: 100%;
          height: auto;
        }

        .calibration-stats {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-bottom: 20px;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .result-icon {
          width: 64px;
          height: 64px;
          background: #FF6432;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: white;
        }

        .calculation-steps {
          background: var(--bg-dark);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
          text-align: left;
        }

        .calculation-steps p {
          margin-bottom: 12px;
          color: var(--text-secondary);
          font-size: 13px;
        }

        .equation {
          font-family: 'Courier New', monospace;
          padding: 8px 12px;
          background: var(--bg-elevated);
          border-radius: 4px;
          margin-bottom: 8px;
          font-size: 13px;
        }

        .equation.small {
          font-size: 11px;
        }

        .equation.result {
          background: #FF6432;
          color: white;
          font-weight: bold;
        }

        .divider {
          height: 1px;
          background: var(--border);
          margin: 16px 0;
        }

        .modal-close-btn {
          padding: 12px 32px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .modal-close-btn:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

export default Part3Measurement;
